import {
  ArrowLeftRight,
  Eye,
  RefreshCw,
  Search,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import DetalleMovimientoBodegaModal from '../../components/bodega/DetalleMovimientoBodegaModal'
import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'
import {
  formatearFechaBodega,
  obtenerTipoMovimiento,
  TIPOS_MOVIMIENTO_BODEGA,
} from '../../utils/bodega'

function MovimientosBodega() {
  const { empresaActual } = useEmpresa()
  const location = useLocation()
  const navigate = useNavigate()

  const [movimientos, setMovimientos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')

  const [busqueda, setBusqueda] = useState('')
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroBodega, setFiltroBodega] = useState('')
  const [filtroDesde, setFiltroDesde] = useState('')
  const [filtroHasta, setFiltroHasta] = useState('')

  const [movimientoDetalle, setMovimientoDetalle] = useState(null)
  const [detalleAbierto, setDetalleAbierto] = useState(false)

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setMovimientos([])
      setCargando(false)
      return
    }

    setBusqueda('')
    setFiltroTipo('')
    setFiltroBodega('')
    setFiltroDesde('')
    setFiltroHasta('')

    cargarMovimientos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  useEffect(() => {
    if (!location.state) return

    if (location.state.recargar) {
      cargarMovimientos()
    }

    navigate('/bodega/movimientos', { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const cargarMovimientos = async () => {
    if (!empresaActual?.id) return

    try {
      setCargando(true)
      setError('')

      const { data, error: errorConsulta } = await supabase
        .from('movimientos_bodega')
        .select(`
          id,
          empresa_id,
          tipo_movimiento,
          fecha,
          bodega_origen_id,
          bodega_destino_id,
          tipo_documento,
          numero_documento,
          referencia,
          observaciones,
          estado,
          creado_por,
          creado_en,
          actualizado_en,
          bodega_origen:bodegas!fk_movimiento_bodega_origen_empresa(
            id,
            codigo,
            nombre
          ),
          bodega_destino:bodegas!fk_movimiento_bodega_destino_empresa(
            id,
            codigo,
            nombre
          )
        `)
        .eq('empresa_id', empresaActual.id)
        .order('fecha', { ascending: false })
        .order('creado_en', { ascending: false })

      if (errorConsulta) {
        throw errorConsulta
      }

      if (!montadoRef.current) return

      setMovimientos(data || [])
    } catch (errorCarga) {
      console.error(
        'Error al cargar movimientos de bodega:',
        errorCarga
      )

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar los movimientos de bodega.'
      )
      setMovimientos([])
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const bodegasDisponibles = useMemo(() => {
    const mapa = new Map()

    movimientos.forEach((movimiento) => {
      ;[movimiento.bodega_origen, movimiento.bodega_destino].forEach(
        (bodega) => {
          if (bodega && !mapa.has(bodega.id)) {
            mapa.set(bodega.id, bodega)
          }
        }
      )
    })

    return Array.from(mapa.values()).sort((a, b) =>
      (a.codigo || '').localeCompare(b.codigo || '')
    )
  }, [movimientos])

  const movimientosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase()

    return movimientos.filter((movimiento) => {
      const coincideBusqueda =
        !termino ||
        (movimiento.tipo_documento || '')
          .toLowerCase()
          .includes(termino) ||
        (movimiento.numero_documento || '')
          .toLowerCase()
          .includes(termino) ||
        (movimiento.referencia || '')
          .toLowerCase()
          .includes(termino) ||
        (movimiento.observaciones || '')
          .toLowerCase()
          .includes(termino)

      const coincideTipo =
        !filtroTipo || movimiento.tipo_movimiento === filtroTipo

      const coincideBodega =
        !filtroBodega ||
        movimiento.bodega_origen_id === filtroBodega ||
        movimiento.bodega_destino_id === filtroBodega

      const coincideDesde =
        !filtroDesde || movimiento.fecha >= filtroDesde

      const coincideHasta =
        !filtroHasta || movimiento.fecha <= filtroHasta

      return (
        coincideBusqueda &&
        coincideTipo &&
        coincideBodega &&
        coincideDesde &&
        coincideHasta
      )
    })
  }, [
    movimientos,
    busqueda,
    filtroTipo,
    filtroBodega,
    filtroDesde,
    filtroHasta,
  ])

  const hayFiltrosActivos =
    Boolean(busqueda.trim()) ||
    Boolean(filtroTipo) ||
    Boolean(filtroBodega) ||
    Boolean(filtroDesde) ||
    Boolean(filtroHasta)

  const limpiarFiltros = () => {
    setBusqueda('')
    setFiltroTipo('')
    setFiltroBodega('')
    setFiltroDesde('')
    setFiltroHasta('')
  }

  const verMovimiento = (movimiento) => {
    setMovimientoDetalle(movimiento)
    setDetalleAbierto(true)
  }

  const cerrarDetalle = () => {
    setDetalleAbierto(false)
    setMovimientoDetalle(null)
  }

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-64 items-center justify-center">
          <div className="text-center">
            <RefreshCw
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando movimientos...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">{error}</p>

          <button
            type="button"
            onClick={cargarMovimientos}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (movimientos.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <ArrowLeftRight
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            Aún no existen movimientos de bodega
          </h3>

          <p className="mt-2 text-slate-500">
            Los movimientos que registres aparecerán en este
            listado.
          </p>
        </div>
      )
    }

    return (
      <>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <div className="relative sm:max-w-xs sm:flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar por documento, número o referencia"
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <select
            value={filtroTipo}
            onChange={(event) => setFiltroTipo(event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todos los tipos</option>

            {Object.entries(TIPOS_MOVIMIENTO_BODEGA).map(
              ([valor, info]) => (
                <option
                  key={valor}
                  value={valor}
                >
                  {info.etiqueta}
                </option>
              )
            )}
          </select>

          <select
            value={filtroBodega}
            onChange={(event) => setFiltroBodega(event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          >
            <option value="">Todas las bodegas</option>

            {bodegasDisponibles.map((bodega) => (
              <option
                key={bodega.id}
                value={bodega.id}
              >
                {bodega.codigo} — {bodega.nombre}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={filtroDesde}
            onChange={(event) => setFiltroDesde(event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          />

          <input
            type="date"
            value={filtroHasta}
            onChange={(event) => setFiltroHasta(event.target.value)}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 sm:w-auto"
          />
        </div>

        {movimientosFiltrados.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <p className="text-slate-500">
              No se encontraron movimientos para los filtros
              aplicados.
            </p>

            {hayFiltrosActivos && (
              <button
                type="button"
                onClick={limpiarFiltros}
                className="mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm lg:min-w-[960px]">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Fecha</th>
                  <th className="px-6 py-3">Tipo</th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Origen
                  </th>
                  <th className="hidden px-6 py-3 md:table-cell">
                    Destino
                  </th>
                  <th className="hidden px-6 py-3 lg:table-cell">
                    Documento
                  </th>
                  <th className="hidden px-6 py-3 lg:table-cell">
                    Referencia
                  </th>
                  <th className="px-6 py-3">Estado</th>
                  <th className="px-6 py-3 text-right">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {movimientosFiltrados.map((movimiento) => {
                  const tipo = obtenerTipoMovimiento(
                    movimiento.tipo_movimiento
                  )

                  return (
                    <tr key={movimiento.id}>
                      <td className="whitespace-nowrap px-6 py-4 text-slate-600">
                        {formatearFechaBodega(movimiento.fecha)}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${tipo.clase}`}
                        >
                          {tipo.etiqueta}
                        </span>
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 md:table-cell">
                        {movimiento.bodega_origen
                          ? `${movimiento.bodega_origen.codigo}`
                          : '—'}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 md:table-cell">
                        {movimiento.bodega_destino
                          ? `${movimiento.bodega_destino.codigo}`
                          : '—'}
                      </td>

                      <td className="hidden whitespace-nowrap px-6 py-4 text-slate-600 lg:table-cell">
                        {movimiento.tipo_documento || '—'}

                        {movimiento.numero_documento
                          ? ` N.º ${movimiento.numero_documento}`
                          : ''}
                      </td>

                      <td
                        className="hidden max-w-xs truncate px-6 py-4 text-slate-600 lg:table-cell"
                        title={movimiento.referencia || ''}
                      >
                        {movimiento.referencia || '—'}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4">
                        {movimiento.estado === 'anulado' ? (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                            Anulado
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                            Registrado
                          </span>
                        )}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => verMovimiento(movimiento)}
                          title="Ver"
                          aria-label="Ver movimiento"
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
                        >
                          <Eye size={16} />
                          Ver
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Bodega
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Movimientos de bodega
          </h2>

          <p className="mt-2 text-slate-500">
            Historial de movimientos para{' '}
            {empresaActual?.nombre_fantasia}.
          </p>
        </div>

        <button
          type="button"
          onClick={cargarMovimientos}
          disabled={cargando}
          className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw
            size={18}
            className={cargando ? 'animate-spin' : ''}
          />
          Actualizar
        </button>
      </div>

      {renderContenido()}

      <DetalleMovimientoBodegaModal
        abierto={detalleAbierto}
        movimiento={movimientoDetalle}
        onCerrar={cerrarDetalle}
      />
    </div>
  )
}

export default MovimientosBodega
